import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AccountBalance, Assessment, CalendarMonth, Download, FilterList, Paid, Print, ReceiptLong, TrendingUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import BasicCli from '../../main/components/basicCli/BasicCli';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import IUser from '../../main/interfaces/IUser';
import useGetUser from '../../main/hooks/useGetUser';
import { useAppTheme } from '../../main/theme/AppThemeContext';
import './CompanyBalance.css';

type InvoiceType = 'all' | 'cadra' | 'restaurant' | 'bar' | 'playground';

interface CompanyBalanceRequest {
  userId: number;
  invoiceType: InvoiceType;
  dateFrom: string;
  dateTo: string;
  fromDate: string;
  toDate: string;
  startDate: string;
  endDate: string;
}

interface CompanyBalanceLine {
  id: string;
  source: string;
  invoiceCount: number;
  quantity: number;
  revenue: number;
}

interface CompanyBalanceReport {
  companyName?: string;
  periodLabel: string;
  dateFrom: string;
  dateTo: string;
  generatedAt: string;
  totalRevenue: number;
  paidRevenue: number;
  unpaidRevenue: number;
  invoiceCount: number;
  paidInvoiceCount: number;
  unpaidInvoiceCount: number;
  averageInvoiceValue: number;
  templateBytes?: string;
  templateMimeType?: string;
  lines: CompanyBalanceLine[];
}

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeServerReport = (
  data: any,
  fallback: CompanyBalanceRequest,
  companyName: string | undefined,
  getItemFallback: (index: number) => string,
): CompanyBalanceReport | null => {
  if (!data || Array.isArray(data)) return null;

  const linesSource = data.lines ?? data.items ?? data.revenueLines ?? data.breakdown ?? [];
  const lines: CompanyBalanceLine[] = (Array.isArray(linesSource) ? linesSource : []).map((item: any, index: number) => ({
    id: String(item.id ?? item.productId ?? item.sourceId ?? index),
    source: item.source ?? item.name ?? item.productName ?? item.label ?? getItemFallback(index + 1),
    invoiceCount: Number(item.invoiceCount ?? item.invoices ?? item.count ?? 0),
    quantity: Number(item.quantity ?? item.totalQuantity ?? 0),
    revenue: Number(item.revenue ?? item.totalRevenue ?? item.totalValue ?? item.value ?? 0),
  }));

  const totalRevenue = Number(data.totalRevenue ?? data.revenue ?? data.totalValue ?? lines.reduce((sum, item) => sum + item.revenue, 0));
  const invoiceCount = Number(data.invoiceCount ?? data.totalInvoices ?? lines.reduce((sum, item) => sum + item.invoiceCount, 0));
  const paidRevenue = Number(data.paidRevenue ?? data.paidTotal ?? 0);
  const unpaidRevenue = Number(data.unpaidRevenue ?? data.unpaidTotal ?? Math.max(0, totalRevenue - paidRevenue));
  const paidInvoiceCount = Number(data.paidInvoiceCount ?? 0);
  const unpaidInvoiceCount = Number(data.unpaidInvoiceCount ?? Math.max(0, invoiceCount - paidInvoiceCount));

  return {
    companyName: data.companyName ?? companyName,
    periodLabel: data.periodLabel ?? data.dateRange ?? `${fallback.dateFrom} - ${fallback.dateTo}`,
    dateFrom: data.dateFrom ?? data.fromDate ?? fallback.dateFrom,
    dateTo: data.dateTo ?? data.toDate ?? fallback.dateTo,
    generatedAt: data.generatedAt ?? new Date().toISOString(),
    totalRevenue,
    paidRevenue,
    unpaidRevenue,
    invoiceCount,
    paidInvoiceCount,
    unpaidInvoiceCount,
    averageInvoiceValue: Number(data.averageInvoiceValue ?? data.avgInvoice ?? (invoiceCount ? totalRevenue / invoiceCount : 0)),
    templateBytes: data.templateBytes,
    templateMimeType: data.templateMimeType,
    lines,
  };
};

const decodeTemplateBytes = (value?: string) => {
  if (!value) return '';

  try {
    const binary = window.atob(value);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  } catch (e) {
    console.error(e);
    return '';
  }
};

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const escapeCsvCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const translateReportTemplateHtml = (html: string, i18n: any) => {
  if (!html) return '';

  const replacements: Array<[RegExp, string]> = [
    [/>Report template</g, `>${i18n.t('companyBalance.sections.reportTemplate', { defaultValue: 'Report template' })}<`],
    [/>Summary view for printing</g, `>${i18n.t('companyBalance.sections.printSummary', { defaultValue: 'Summary view for printing' })}<`],
    [/>Bilanc i kompanise</g, `>${i18n.t('companyBalance.title', { defaultValue: 'Company balance' })}<`],
    [/>Kompania</g, `>${i18n.t('companyBalance.fallback.company', { defaultValue: 'Company' })}<`],
    [/>Gjeneruar:/g, `>${i18n.t('companyBalance.report.generatedAtLabel', { defaultValue: 'Generated' })}:`],
    [/>TE ARDHURA TOTALE</g, `>${i18n.t('companyBalance.stats.totalRevenue', { defaultValue: 'Total revenue' }).toUpperCase()}<`],
    [/>Te ardhura totale</g, `>${i18n.t('companyBalance.stats.totalRevenue', { defaultValue: 'Total revenue' })}<`],
    [/>Te paguara</g, `>${i18n.t('companyBalance.stats.paidRevenue', { defaultValue: 'Paid' })}<`],
    [/>Te papaguara</g, `>${i18n.t('companyBalance.stats.unpaidRevenue', { defaultValue: 'Unpaid' })}<`],
    [/>Mesatarja\/fature</g, `>${i18n.t('companyBalance.stats.averageInvoice', { defaultValue: 'Average/invoice' })}<`],
    [/>BURIMI</g, `>${i18n.t('companyBalance.table.item', { defaultValue: 'Item' }).toUpperCase()}<`],
    [/>Burimi</g, `>${i18n.t('companyBalance.table.item', { defaultValue: 'Item' })}<`],
    [/>FATURA</g, `>${i18n.t('companyBalance.table.invoices', { defaultValue: 'Invoices' }).toUpperCase()}<`],
    [/>Fatura</g, `>${i18n.t('companyBalance.table.invoices', { defaultValue: 'Invoices' })}<`],
    [/>SASIA</g, `>${i18n.t('companyBalance.table.quantity', { defaultValue: 'Quantity' }).toUpperCase()}<`],
    [/>Sasia</g, `>${i18n.t('companyBalance.table.quantity', { defaultValue: 'Quantity' })}<`],
    [/>TE ARDHURA</g, `>${i18n.t('companyBalance.table.revenue', { defaultValue: 'Revenue' }).toUpperCase()}<`],
    [/>Te ardhura</g, `>${i18n.t('companyBalance.table.revenue', { defaultValue: 'Revenue' })}<`],
    [/>Nuk ka te ardhura per filtrat e zgjedhur\.</g, `>${i18n.t('companyBalance.messages.noRevenueForFilters', { defaultValue: 'There is no revenue for the selected filters.' })}<`],
  ];

  return replacements.reduce((result, [pattern, replacement]) => result.replace(pattern, replacement), html);
};

const CompanyBalance: React.FC = () => {
  const { i18n } = useTranslation();
  const { theme } = useAppTheme();
  const user = useGetUser();
  const currencySymbol = user?.companyCurrencySymbol ?? '';
  const [users, setUsers] = useState<IUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [invoiceType, setInvoiceType] = useState<InvoiceType>('all');
  const [dateFrom, setDateFrom] = useState(formatDateForInput(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [dateTo, setDateTo] = useState(formatDateForInput(new Date()));
  const [draftDateFrom, setDraftDateFrom] = useState(dateFrom);
  const [draftDateTo, setDraftDateTo] = useState(dateTo);
  const [showDateModal, setShowDateModal] = useState(false);
  const [report, setReport] = useState<CompanyBalanceReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  const formatCurrency = useCallback(
    (value: number) =>
      `${new Intl.NumberFormat(i18n.language || 'sq-AL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(value || 0))}${currencySymbol ? ` ${currencySymbol}` : ''}`,
    [currencySymbol, i18n.language],
  );

  const requestPayload = useMemo<CompanyBalanceRequest>(() => {
    return {
      userId: selectedUserId,
      invoiceType,
      dateFrom,
      dateTo,
      fromDate: dateFrom,
      toDate: dateTo,
      startDate: dateFrom,
      endDate: dateTo,
    };
  }, [dateFrom, dateTo, invoiceType, selectedUserId]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axiosInstance.get(ApiConstants.User.users);
      const activeUsers: IUser[] = (response.data ?? []).filter((item: IUser) => !item.isDelete);
      setUsers(activeUsers);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setInfoMessage('');
      setPage(0);

      const response = await axiosInstance.post(ApiConstants.companyBalance.report, requestPayload);
      const serverReport = normalizeServerReport(
        response.data,
        requestPayload,
        user?.companyName,
        (index) => i18n.t('companyBalance.fallback.item', { defaultValue: 'Item #{{index}}', index }),
      );

      if (!serverReport) {
        setReport(null);
        setInfoMessage(i18n.t('companyBalance.messages.invalidReport', { defaultValue: 'The backend did not return a valid report.' }));
        return;
      }

      setReport(serverReport);
      if (!serverReport.lines.length) {
        setInfoMessage(i18n.t('companyBalance.messages.noRevenueForFilters', { defaultValue: 'There is no revenue for the selected filters.' }));
      }
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || i18n.t('companyBalance.messages.loadError', { defaultValue: 'The company balance report could not be loaded.' }));
    } finally {
      setLoading(false);
    }
  }, [i18n, requestPayload, user?.companyName]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openDateModal = useCallback(() => {
    setDraftDateFrom(dateFrom);
    setDraftDateTo(dateTo);
    setShowDateModal(true);
  }, [dateFrom, dateTo]);

  const applyDateModal = useCallback(() => {
    if (draftDateFrom <= draftDateTo) {
      setDateFrom(draftDateFrom);
      setDateTo(draftDateTo);
    } else {
      setDateFrom(draftDateTo);
      setDateTo(draftDateFrom);
    }

    setShowDateModal(false);
  }, [draftDateFrom, draftDateTo]);

  const paginatedLines = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return (report?.lines ?? []).slice(startIndex, startIndex + rowsPerPage);
  }, [page, report?.lines, rowsPerPage]);

  const reportTemplateHtml = useMemo(
    () => translateReportTemplateHtml(decodeTemplateBytes(report?.templateBytes), i18n),
    [i18n, report?.templateBytes],
  );

  const printReport = useCallback(() => {
    if (!report) return;

    const locale = i18n.language || 'sq-AL';
    const generatedAt = new Date(report.generatedAt).toLocaleString(locale);
    const quantityFormatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    const rowsHtml = report.lines.length
      ? report.lines
          .map(
            (line) => `
              <tr>
                <td>${escapeHtml(line.source)}</td>
                <td class="num">${escapeHtml(line.invoiceCount)}</td>
                <td class="num">${escapeHtml(quantityFormatter.format(Number(line.quantity || 0)))}</td>
                <td class="money">${escapeHtml(formatCurrency(line.revenue))}</td>
              </tr>`,
          )
          .join('')
      : `<tr><td colspan="4" class="empty">${escapeHtml(
          i18n.t('companyBalance.messages.noRevenueForFilters', {
            defaultValue: 'There is no revenue for the selected filters.',
          }),
        )}</td></tr>`;

    const html = `<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(i18n.t('companyBalance.title', { defaultValue: 'Company balance' }))}</title>
          <style>
            @page { size: A4; margin: 14mm; }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              color: #08162f;
              background: #ffffff;
              font-family: Inter, Arial, sans-serif;
              font-size: 12px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .sheet { width: 100%; }
            .top {
              display: flex;
              justify-content: space-between;
              gap: 18px;
              align-items: flex-start;
              padding-bottom: 14px;
              border-bottom: 3px solid #08162f;
              margin-bottom: 18px;
            }
            h1 { margin: 0 0 4px; font-size: 28px; line-height: 1.1; }
            .company { font-size: 14px; font-weight: 700; }
            .meta { text-align: right; line-height: 1.55; white-space: nowrap; }
            .period { font-weight: 900; }
            .hero {
              border: 1px solid #cbd8ea;
              padding: 14px 16px;
              margin-bottom: 14px;
            }
            .label {
              font-size: 11px;
              text-transform: uppercase;
              font-weight: 900;
              margin-bottom: 6px;
            }
            .total { font-size: 30px; font-weight: 950; line-height: 1; }
            .stats {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
              margin-bottom: 18px;
            }
            .stat {
              border: 1px solid #cbd8ea;
              padding: 11px 12px;
            }
            .statValue { font-size: 20px; font-weight: 950; }
            table { width: 100%; border-collapse: collapse; }
            thead { display: table-header-group; }
            tr { page-break-inside: avoid; }
            th {
              text-align: left;
              font-size: 11px;
              font-weight: 950;
              text-transform: uppercase;
              border-bottom: 2px solid #cbd8ea;
              padding: 9px 8px;
            }
            td {
              border-bottom: 1px solid #dce5f2;
              padding: 8px;
              vertical-align: top;
            }
            .num, .money { text-align: right; }
            .money { font-weight: 950; }
            .empty { text-align: center; color: #64748b; padding: 24px 8px; }
            .footer {
              display: flex;
              justify-content: space-between;
              gap: 12px;
              margin-top: 16px;
              padding-top: 10px;
              border-top: 1px solid #cbd8ea;
              color: #64748b;
              font-size: 11px;
            }
          </style>
        </head>
        <body>
          <main class="sheet">
            <header class="top">
              <div>
                <h1>${escapeHtml(i18n.t('companyBalance.title', { defaultValue: 'Company balance' }))}</h1>
                <div class="company">${escapeHtml(report.companyName || i18n.t('companyBalance.fallback.company', { defaultValue: 'Company' }))}</div>
              </div>
              <div class="meta">
                <div class="period">${escapeHtml(report.periodLabel)}</div>
                <div>${escapeHtml(i18n.t('companyBalance.report.generatedAtLabel', { defaultValue: 'Generated' }))}: ${escapeHtml(generatedAt)}</div>
              </div>
            </header>

            <section class="hero">
              <div class="label">${escapeHtml(i18n.t('companyBalance.stats.totalRevenue', { defaultValue: 'Total revenue' }))}</div>
              <div class="total">${escapeHtml(formatCurrency(report.totalRevenue))}</div>
            </section>

            <section class="stats">
              <div class="stat">
                <div class="label">${escapeHtml(i18n.t('companyBalance.stats.paidRevenue', { defaultValue: 'Paid' }))}</div>
                <div class="statValue">${escapeHtml(formatCurrency(report.paidRevenue))}</div>
              </div>
              <div class="stat">
                <div class="label">${escapeHtml(i18n.t('companyBalance.stats.unpaidRevenue', { defaultValue: 'Unpaid' }))}</div>
                <div class="statValue">${escapeHtml(formatCurrency(report.unpaidRevenue))}</div>
              </div>
              <div class="stat">
                <div class="label">${escapeHtml(i18n.t('companyBalance.stats.averageInvoice', { defaultValue: 'Average/invoice' }))}</div>
                <div class="statValue">${escapeHtml(formatCurrency(report.averageInvoiceValue))}</div>
              </div>
            </section>

            <table>
              <thead>
                <tr>
                  <th>${escapeHtml(i18n.t('companyBalance.table.item', { defaultValue: 'Item' }))}</th>
                  <th class="num">${escapeHtml(i18n.t('companyBalance.table.invoices', { defaultValue: 'Invoices' }))}</th>
                  <th class="num">${escapeHtml(i18n.t('companyBalance.table.quantity', { defaultValue: 'Quantity' }))}</th>
                  <th class="money">${escapeHtml(i18n.t('companyBalance.table.revenue', { defaultValue: 'Revenue' }))}</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
              <tfoot>
                <tr>
                  <td colspan="3">${escapeHtml(i18n.t('companyBalance.report.reportTotal', { defaultValue: 'Report total' }))}</td>
                  <td class="money">${escapeHtml(formatCurrency(report.totalRevenue))}</td>
                </tr>
              </tfoot>
            </table>

            <footer class="footer">
              <span>${escapeHtml(report.dateFrom)} - ${escapeHtml(report.dateTo)}</span>
              <span>${escapeHtml(i18n.t('companyBalance.stats.totalInvoices', { defaultValue: '{{count}} invoices total', count: report.invoiceCount }))}</span>
            </footer>
          </main>
          <script>
            window.addEventListener('load', function () {
              window.focus();
              window.print();
            });
          </script>
        </body>
      </html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=900');
    if (!printWindow) {
      setError(
        i18n.t('companyBalance.messages.printPopupBlocked', {
          defaultValue: 'The print window could not be opened. Please allow pop-ups and try again.',
        }),
      );
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }, [formatCurrency, i18n, report]);

  const exportCsv = useCallback(() => {
    if (!report) return;

    const locale = i18n.language || 'sq-AL';
    const selectedUser = users.find((item) => item.id === selectedUserId);
    const selectedUserLabel =
      selectedUserId === 0
        ? i18n.t('companyBalance.filters.allUsers', { defaultValue: 'All' })
        : selectedUser?.userName ||
          `${selectedUser?.firstName ?? ''} ${selectedUser?.lastName ?? ''}`.trim() ||
          i18n.t('companyBalance.fallback.user', { defaultValue: 'User #{{id}}', id: selectedUserId });
    const invoiceTypeLabel = i18n.t(`companyBalance.invoiceTypes.${invoiceType}`, { defaultValue: invoiceType });
    const generatedAt = new Date(report.generatedAt).toLocaleString(locale);
    const headers = [
      i18n.t('companyBalance.table.item', { defaultValue: 'Item' }),
      i18n.t('companyBalance.table.invoices', { defaultValue: 'Invoices' }),
      i18n.t('companyBalance.table.quantity', { defaultValue: 'Quantity' }),
      i18n.t('companyBalance.table.revenue', { defaultValue: 'Revenue' }),
    ];
    const rows = report.lines.map((line) => [line.source, line.invoiceCount, line.quantity, line.revenue]);
    const summaryRows = [
      [i18n.t('companyBalance.title', { defaultValue: 'Company balance' })],
      [i18n.t('companyBalance.fallback.company', { defaultValue: 'Company' }), report.companyName || i18n.t('companyBalance.fallback.company', { defaultValue: 'Company' })],
      [i18n.t('companyBalance.filters.user', { defaultValue: 'User' }), selectedUserLabel],
      [i18n.t('companyBalance.export.filterDate', { defaultValue: 'Filter date' }), `${report.dateFrom} - ${report.dateTo}`],
      [i18n.t('companyBalance.filters.invoiceType', { defaultValue: 'Invoice type' }), invoiceTypeLabel],
      [i18n.t('companyBalance.report.generatedAtLabel', { defaultValue: 'Generated' }), generatedAt],
      [],
      [i18n.t('companyBalance.stats.totalRevenue', { defaultValue: 'Total revenue' }), formatCurrency(report.totalRevenue)],
      [i18n.t('companyBalance.stats.paidRevenue', { defaultValue: 'Paid' }), formatCurrency(report.paidRevenue)],
      [i18n.t('companyBalance.stats.unpaidRevenue', { defaultValue: 'Unpaid' }), formatCurrency(report.unpaidRevenue)],
      [i18n.t('companyBalance.stats.averageInvoice', { defaultValue: 'Average/invoice' }), formatCurrency(report.averageInvoiceValue)],
      [i18n.t('companyBalance.stats.totalInvoices', { defaultValue: '{{count}} invoices total', count: report.invoiceCount })],
      [],
    ];
    const csv = [summaryRows, [headers, ...rows]]
      .flat()
      .map((row) => row.map(escapeCsvCell).join(','))
      .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${i18n.t('companyBalance.export.fileName', { defaultValue: 'company-balance' })}-${report.dateFrom}-${report.dateTo}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [formatCurrency, i18n, invoiceType, report, selectedUserId, users]);

  const totalForPercent = Math.max(1, report?.totalRevenue ?? 0);

  return (
    <BasicCli>
      <div className="cbPage">
        <header className="cbHeader cbCard">
          <div>
            <Typography className="cbTitle" variant="h4">
              {i18n.t('companyBalance.title', { defaultValue: 'Company balance' })}
            </Typography>
            <Typography className="cbSubtitle">
              {i18n.t('companyBalance.subtitle', {
                defaultValue: 'Report template for company revenue. The backend can provide final values from the balance endpoint.',
              })}
            </Typography>
          </div>
          <AccountBalance className="cbHeaderIcon" />
        </header>

        {(error || infoMessage) && (
          <div style={{ marginBottom: 16 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {infoMessage && <Alert severity="info">{infoMessage}</Alert>}
          </div>
        )}

        <section className="cbFilters cbCard">
          <div className="cbFiltersHead">
            <Typography className="cbFiltersTitle">
              <FilterList fontSize="small" />
              {i18n.t('companyBalance.filters.title', { defaultValue: 'Report filters' })}
            </Typography>
            <div className="cbFiltersActions">
              <Button className="cbBtn" variant="outlined" startIcon={<Print />} onClick={printReport} disabled={!report}>
                {i18n.t('companyBalance.actions.print', { defaultValue: 'Print' })}
              </Button>
              <Button className="cbBtn" variant="contained" startIcon={<Download />} onClick={exportCsv} disabled={!report}>
                {i18n.t('companyBalance.actions.exportCsv', { defaultValue: 'Export CSV' })}
              </Button>
            </div>
          </div>

          <div className="cbFiltersGrid">
            <FormControl fullWidth size="small">
              <InputLabel>{i18n.t('companyBalance.filters.user', { defaultValue: 'User' })}</InputLabel>
              <Select value={selectedUserId} label={i18n.t('companyBalance.filters.user', { defaultValue: 'User' })} onChange={(e) => setSelectedUserId(Number(e.target.value))}>
                <MenuItem value={0}>{i18n.t('companyBalance.filters.allUsers', { defaultValue: 'All' })}</MenuItem>
                {users.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.userName || `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim() || i18n.t('companyBalance.fallback.user', { defaultValue: 'User #{{id}}', id: item.id })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button className="cbBtn cbDateBtn" variant="outlined" startIcon={<CalendarMonth />} onClick={openDateModal}>
              {dateFrom} - {dateTo}
            </Button>

            <FormControl fullWidth size="small">
              <InputLabel>{i18n.t('companyBalance.filters.invoiceType', { defaultValue: 'Invoice type' })}</InputLabel>
              <Select value={invoiceType} label={i18n.t('companyBalance.filters.invoiceType', { defaultValue: 'Invoice type' })} onChange={(e) => setInvoiceType(e.target.value as InvoiceType)}>
                <MenuItem value="all">{i18n.t('companyBalance.invoiceTypes.all', { defaultValue: 'All' })}</MenuItem>
                <MenuItem value="cadra">{i18n.t('companyBalance.invoiceTypes.cadra', { defaultValue: 'Umbrellas' })}</MenuItem>
                <MenuItem value="restaurant">{i18n.t('companyBalance.invoiceTypes.restaurant', { defaultValue: 'Restaurant' })}</MenuItem>
                <MenuItem value="bar">{i18n.t('companyBalance.invoiceTypes.bar', { defaultValue: 'Bar' })}</MenuItem>
                <MenuItem value="playground">{i18n.t('companyBalance.invoiceTypes.playground', { defaultValue: 'Playground' })}</MenuItem>
              </Select>
            </FormControl>

            <Button className="cbBtn" variant="contained" startIcon={<Assessment />} onClick={() => loadReport()} disabled={loading}>
              {i18n.t('companyBalance.actions.generateReport', { defaultValue: 'Generate report' })}
            </Button>
          </div>
        </section>

        <Dialog open={showDateModal} onClose={() => setShowDateModal(false)} maxWidth="xs" fullWidth PaperProps={{ className: `cbDateDialogPaper bcTheme-${theme}` }}>
          <DialogTitle className="cbDialogTitle">
            <span>{i18n.t('companyBalance.dateDialog.title', { defaultValue: 'Choose dates' })}</span>
          </DialogTitle>
          <DialogContent className="cbDialogBody">
            <div className="cbDateModalGrid">
              <TextField
                label={i18n.t('companyBalance.dateDialog.dateFrom', { defaultValue: 'Date from' })}
                type="date"
                size="small"
                value={draftDateFrom}
                onChange={(e) => setDraftDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label={i18n.t('companyBalance.dateDialog.dateTo', { defaultValue: 'Date to' })}
                type="date"
                size="small"
                value={draftDateTo}
                onChange={(e) => setDraftDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </div>
          </DialogContent>
          <DialogActions className="cbDialogActions">
            <Button onClick={() => setShowDateModal(false)}>{i18n.t('companyBalance.actions.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button variant="contained" onClick={applyDateModal}>
              {i18n.t('companyBalance.actions.apply', { defaultValue: 'Apply' })}
            </Button>
          </DialogActions>
        </Dialog>

        {loading ? (
          <div className="cbCenter cbCard">
            <CircularProgress />
            <Typography className="cbMuted">{i18n.t('companyBalance.loading', { defaultValue: 'Generating report...' })}</Typography>
          </div>
        ) : !report ? (
          <div className="cbEmpty cbCard">
            <Assessment className="cbEmptyIcon" />
            <Typography className="cbEmptyTitle">{i18n.t('companyBalance.empty.noReport', { defaultValue: 'No report to display' })}</Typography>
          </div>
        ) : (
          <>
            <section className="cbStats">
              <div className="cbStat cbStatPrimary cbCard">
                <div className="cbStatTop">
                  <Typography className="cbStatLabel">{i18n.t('companyBalance.stats.totalRevenue', { defaultValue: 'Total revenue' })}</Typography>
                  <TrendingUp className="cbStatIcon" />
                </div>
                <div className="cbStatValue">{formatCurrency(report.totalRevenue)}</div>
                <div className="cbStatHint">{report.periodLabel}</div>
              </div>
              <div className="cbStat cbCard">
                <div className="cbStatTop">
                  <Typography className="cbStatLabel">{i18n.t('companyBalance.stats.paidRevenue', { defaultValue: 'Paid' })}</Typography>
                  <Paid className="cbStatIcon" />
                </div>
                <div className="cbStatValue">{formatCurrency(report.paidRevenue)}</div>
                <div className="cbStatHint">{i18n.t('companyBalance.stats.invoiceCount', { defaultValue: '{{count}} invoices', count: report.paidInvoiceCount })}</div>
              </div>
              <div className="cbStat cbCard">
                <div className="cbStatTop">
                  <Typography className="cbStatLabel">{i18n.t('companyBalance.stats.unpaidRevenue', { defaultValue: 'Unpaid' })}</Typography>
                  <ReceiptLong className="cbStatIcon" />
                </div>
                <div className="cbStatValue">{formatCurrency(report.unpaidRevenue)}</div>
                <div className="cbStatHint">{i18n.t('companyBalance.stats.invoiceCount', { defaultValue: '{{count}} invoices', count: report.unpaidInvoiceCount })}</div>
              </div>
              <div className="cbStat cbCard">
                <div className="cbStatTop">
                  <Typography className="cbStatLabel">{i18n.t('companyBalance.stats.averageInvoice', { defaultValue: 'Average/invoice' })}</Typography>
                  <Assessment className="cbStatIcon" />
                </div>
                <div className="cbStatValue">{formatCurrency(report.averageInvoiceValue)}</div>
                <div className="cbStatHint">{i18n.t('companyBalance.stats.totalInvoices', { defaultValue: '{{count}} invoices total', count: report.invoiceCount })}</div>
              </div>
            </section>

            <section className="cbLayout">
              <div className="cbPanel cbCard">
                <div className="cbPanelHead">
                  <div className="cbPanelTitle">{i18n.t('companyBalance.sections.revenueByItem', { defaultValue: 'Revenue by item' })}</div>
                  <div className="cbPanelSub cbMuted">{i18n.t('companyBalance.sections.balanceTable', { defaultValue: 'Balance report table' })}</div>
                </div>
                <TableContainer component={Paper} className="cbTableWrap">
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className="cbTh">{i18n.t('companyBalance.table.item', { defaultValue: 'Item' })}</TableCell>
                        <TableCell className="cbTh" align="right">
                          {i18n.t('companyBalance.table.invoices', { defaultValue: 'Invoices' })}
                        </TableCell>
                        <TableCell className="cbTh" align="right">
                          {i18n.t('companyBalance.table.quantity', { defaultValue: 'Quantity' })}
                        </TableCell>
                        <TableCell className="cbTh" align="right">
                          {i18n.t('companyBalance.table.revenue', { defaultValue: 'Revenue' })}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedLines.map((line) => (
                        <TableRow key={line.id} hover>
                          <TableCell>{line.source}</TableCell>
                          <TableCell align="right">{line.invoiceCount}</TableCell>
                          <TableCell align="right">{line.quantity}</TableCell>
                          <TableCell align="right" className="cbMoney">
                            {formatCurrency(line.revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={report.lines.length}
                  page={page}
                  onPageChange={(_, nextPage) => setPage(nextPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 8, 15, 25]}
                  labelRowsPerPage={i18n.t('companyBalance.pagination.rowsPerPage', { defaultValue: 'Rows per page:' })}
                  labelDisplayedRows={({ from, to, count }) =>
                    i18n.t('companyBalance.pagination.displayedRows', {
                      defaultValue: '{{from}}-{{to}} of {{total}}',
                      from,
                      to,
                      total: count === -1 ? i18n.t('companyBalance.pagination.moreThan', { defaultValue: 'more than {{to}}', to }) : String(count),
                    })
                  }
                />
              </div>

              <aside className="cbPanel cbCard">
                <div className="cbPanelHead">
                  <div className="cbPanelTitle">{i18n.t('companyBalance.sections.reportTemplate', { defaultValue: 'Report template' })}</div>
                  <div className="cbPanelSub cbMuted">{i18n.t('companyBalance.sections.printSummary', { defaultValue: 'Summary view for printing' })}</div>
                </div>
                {reportTemplateHtml ? (
                  <div className="cbTemplateFrame" dangerouslySetInnerHTML={{ __html: reportTemplateHtml }} />
                ) : (
                  <div className="cbReport">
                    <div>
                      <Typography variant="h6" style={{ fontWeight: 900 }}>
                        {report.companyName || i18n.t('companyBalance.fallback.company', { defaultValue: 'Company' })}
                      </Typography>
                      <Typography className="cbMuted">{i18n.t('companyBalance.report.revenueBalance', { defaultValue: 'Revenue balance: {{period}}', period: report.periodLabel })}</Typography>
                      <Typography className="cbMuted">
                        {i18n.t('companyBalance.report.generatedAt', {
                          defaultValue: 'Generated: {{date}}',
                          date: new Date(report.generatedAt).toLocaleString(i18n.language || 'sq-AL'),
                        })}
                      </Typography>
                    </div>
                    <div className="cbReportBand">
                      <div className="cbReportBandTitle">{i18n.t('companyBalance.report.reportTotal', { defaultValue: 'Report total' })}</div>
                      <div className="cbReportBandValue">{formatCurrency(report.totalRevenue)}</div>
                    </div>
                    <div className="cbBreakdown">
                      {report.lines.slice(0, 6).map((line) => (
                        <div className="cbBreakdownRow" key={line.id}>
                          <div>
                            <div className="cbBreakdownName" title={line.source}>
                              {line.source}
                            </div>
                            <div className="cbProgress">
                              <div className="cbProgressFill" style={{ width: `${Math.min(100, (line.revenue / totalForPercent) * 100)}%` }} />
                            </div>
                          </div>
                          <div className="cbMoney">{formatCurrency(line.revenue)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </aside>
            </section>
          </>
        )}
      </div>
    </BasicCli>
  );
};

export default CompanyBalance;
